import {
  UseMutateAsyncFunction,
  UseMutateFunction,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import { DraftDataDto, UserProfileDto } from "@green0meter/api-client";
import { isEmpty } from "lodash";

import { DOMAIN_FORM_SUBMISSIONS } from "../domain";
import { ACTION_GET_FORM_DRAFT_DATA } from "../GetFormDraftData/useGetFormDraftData";

import { submissionsApi } from "@api/services";

export const ACTION_UPDATE_FORM_DRAFT_DATA_QUESTION =
  "UPDATE_FORM_DRAFT_DATA_QUESTION";

export const updateFormDraftDataByQuestion = async (
  formId: string,
  questionId: string,
): Promise<DraftDataDto[] | undefined> => {
  const response =
    await submissionsApi.submissionsControllerGetFormDraftDataByQuestion(
      formId,
      questionId,
    );

  return response.data;
};

export const useUpdateFormDraftDataByQuestion = (
  user: UserProfileDto | null,
  formId: string,
): {
  mutateFormDraftDataByQuestion: UseMutateFunction<
    DraftDataDto[] | undefined,
    Error,
    string
  >;
  mutateFormDraftDataByQuestionAsync: UseMutateAsyncFunction<
    DraftDataDto[] | undefined,
    Error,
    string
  >;
  isMutatingFormDraftData: boolean;
  isMutationSuccessFormDraftData: boolean;
} => {
  const queryClient = useQueryClient();

  const {
    mutate,
    mutateAsync,
    isPending: isMutationPending,
    isSuccess: isMutationSuccess,
  } = useMutation({
    mutationKey: [
      DOMAIN_FORM_SUBMISSIONS,
      ACTION_UPDATE_FORM_DRAFT_DATA_QUESTION,
      formId,
    ],
    mutationFn: (questionId: string) =>
      updateFormDraftDataByQuestion(formId, questionId),
    onSuccess: (updatedData) => {
      const queryKey = [DOMAIN_FORM_SUBMISSIONS, ACTION_GET_FORM_DRAFT_DATA, formId];
      const currentData = queryClient.getQueryData<DraftDataDto[] | undefined>(
        queryKey,
      );

      if (updatedData && !isEmpty(updatedData)) {
        queryClient.setQueryData(queryKey, () => {
          if (!currentData) return updatedData;

          const currentDataMap = new Map(
            currentData.map((item) => [item.questionCode, item]),
          );

          updatedData.forEach((updatedItem) => {
            currentDataMap.set(updatedItem.questionCode, updatedItem);
          });

          return Array.from(currentDataMap.values());
        });
      }
    },
  });

  return {
    mutateFormDraftDataByQuestion: mutate,
    mutateFormDraftDataByQuestionAsync: mutateAsync,
    isMutatingFormDraftData: isMutationPending,
    isMutationSuccessFormDraftData: isMutationSuccess,
  };
};
